import React, { Component } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { connect } from 'react-redux'
import { reduxForm, Field, formValueSelector } from 'redux-form'
import { openLoader, closeLoader } from '../../../../../../redux-store/loader'
import { closeModal, openModal } from '../../../../../../redux-store/modal'
import PaymentModel from '../../../../../../data/models/payment/payment'
import I18n from '../../../../../../i18n'
import { Form, Button, Message, Icon } from 'semantic-ui-react'
import CustomInput from '../../../../../components/form/input'
import Select from '../../../../../components/form/Select'
import CustomDatePicker from '../../../../../components/form/datepicker'

// Styles
import './extendProLicenses.scss'

const MODES = ['extend', 'reduce', 'set'];

// Mismo techo que aplica el backend. Se repite aquí para avisar antes de enviar, no
// para sustituir la validación: la que manda es la del servidor.
const MAX_YEARS = 5;

/**
 * Ajuste manual de la licencia de un profesor.
 *
 * Tres operaciones distintas en vez de un único campo de días con signo: extender,
 * reducir y establecer una fecha concreta. Con el campo único, "que le caduque el 31
 * de diciembre" obligaba al admin a calcular la diferencia a mano y sobre la fecha
 * que viera en pantalla, que no siempre era la de la licencia que manda.
 */
class ExtendProLicenses extends Component {

  constructor(props) {
    super(props)

    this.state = {
      planOptions: []
    }
  }

  componentDidMount() {
    this.getPlans();
  }

  getPlans = async () => {

    try {

      this.props.openLoader();

      const response = await PaymentModel.getProPlans();
      const plans = response.data;

      const options = plans.map(plan => ({
        key: plan._id,
        text: `${ plan.name } (${ plan.maxStudents } alumnos)`,
        value: plan._id,
        maxStudents: plan.maxStudents
      }));

      this.setState({ planOptions: options });

    } catch (error) {

      console.error("error", error);

    } finally {

      this.props.closeLoader();

    }

  };

  handlePlanChange = (event, newValue) => {

    const { change } = this.props;

    const selectedPlan = this.state.planOptions.find(plan => plan.value === newValue);

    if (selectedPlan && selectedPlan.maxStudents !== undefined) {

      change('maxStudents', selectedPlan.maxStudents);

    } else {

      change('maxStudents', '');

    }

  }

  /**
   * Fecha de fin que quedaría si se aplicase el ajuste tal y como está el
   * formulario. Se calcula igual que en el backend, sobre la fecha de fin de la
   * licencia que manda hoy, para que el admin vea el resultado antes de pulsar.
   */
  previewEnd = () => {

    const { mode, amount, unit, endDate, currentEnd } = this.props;

    if (mode === 'set') {

      return endDate ? moment(endDate) : null;

    }

    if (!currentEnd) return null;

    const quantity = parseInt(amount, 10);

    if (!_.isFinite(quantity) || quantity <= 0) return null;

    const target = mode === 'reduce'
      ? moment(currentEnd).subtract(quantity, unit === 'months' ? 'months' : 'days')
      : moment(currentEnd).add(quantity, unit === 'months' ? 'months' : 'days');

    return target.isBefore(moment(), 'day') ? moment() : target;

  }

  buildPayload = ({ mode, amount, unit, endDate, planId, maxStudents, note }) => {

    const payload = { mode, planId };

    if (mode === 'set') {

      payload.endDate = moment(endDate).format('YYYY-MM-DD');

    } else {

      payload.amount = parseInt(amount, 10);
      payload.unit = unit || 'days';

    }

    if (planId && maxStudents !== undefined && maxStudents !== '') {

      payload.maxStudents = parseInt(maxStudents, 10);

    }

    if (note) payload.note = note;

    return payload;

  }

  apply = async values => {

    const acceptButton = [
      {
        text: I18n.t('actions.accept'),
        callback: () => this.props.closeModal()
      }
    ];

    try {

      this.props.openLoader();

      const response = await PaymentModel.extendProLicense(this.props.userid, this.buildPayload(values));
      const result = response.data || {};
      const stripeSync = result.stripeSync;

      const title = result.action === 'reduced'
        ? I18n.t('pro.info.adjust.doneReduced')
        : I18n.t('pro.info.adjust.doneExtended');

      // El backend cuenta siempre qué ha pasado en Stripe. Se muestra tal cual: la
      // licencia puede haberse aplicado y el cobro seguir su curso (una cancelación
      // programada, un cambio de plan pendiente, un fallo de Stripe), y el admin
      // tiene que enterarse en vez de dar el cambio por hecho.
      const syncFailed = stripeSync && stripeSync.status === 'failed';
      const syncNotApplied = stripeSync && stripeSync.status === 'skipped';
      const showSyncMessage = stripeSync && (syncFailed || syncNotApplied);

      this.props.openModal({
        headerTitle: syncFailed ? "Aviso" : undefined,
        title: (
          <React.Fragment>
            <p>{ title } <strong>{ moment(result.coverageEnd || _.get(result, 'license.endDate')).format('DD/MM/YYYY') }</strong>.</p>
            { result.coverageWarning && <p>{ result.coverageWarning }</p> }
            { showSyncMessage && <p><strong>Stripe:</strong> { stripeSync.message }</p> }
            { !showSyncMessage && stripeSync && stripeSync.status !== 'skipped' && <p><strong>Stripe:</strong> { stripeSync.message }</p> }
          </React.Fragment>
        ),
        buttons: acceptButton
      });

      this.props.reset();
      this.props.onApplied();

    } catch (error) {

      this.props.closeLoader();
      this.props.openModal({
        headerTitle: "Error",
        title: (error.response && error.response.data && error.response.data.error) || I18n.t('messages.modal.commonError'),
        buttons: acceptButton
      });

    } finally {

      this.props.closeLoader();

    }

  }

  /**
   * Recortar una licencia le quita acceso al profesor de inmediato, así que se
   * confirma antes. Extender no: en el peor caso se regala tiempo y se corrige.
   */
  handleSubmitAdjustment = values => {

    const preview = this.previewEnd();
    const { currentEnd } = this.props;

    const isReduction = !!preview && !!currentEnd && preview.isBefore(moment(currentEnd), 'day');

    if (!isReduction) return this.apply(values);

    this.props.openModal({
      headerTitle: I18n.t('pro.info.adjust.confirmTitle'),
      title: I18n.t('pro.info.adjust.confirmReduce', {
        from: moment(currentEnd).format('DD/MM/YYYY'),
        to: preview.format('DD/MM/YYYY')
      }),
      buttons: [
        {
          text: I18n.t('actions.cancel'),
          callback: () => this.props.closeModal()
        },
        {
          text: I18n.t('actions.accept'),
          callback: () => {
            this.props.closeModal();
            this.apply(values);
          }
        }
      ]
    });

  }

  render() {

    const { mode, currentEnd, invalid } = this.props;
    const preview = this.previewEnd();

    const modeOptions = MODES.map(value => ({
      key: value,
      text: I18n.t(`pro.info.adjust.mode.${ value }`),
      value
    }));

    const unitOptions = [
      { key: 'days', text: I18n.t('pro.info.adjust.unit.days'), value: 'days' },
      { key: 'months', text: I18n.t('pro.info.adjust.unit.months'), value: 'months' }
    ];

    return (
      <Form className="proLicenseForm" name="proLicenseForm" onSubmit={ this.props.handleSubmit(this.handleSubmitAdjustment) }>

        <div className='proLicenseFormFields'>

          <Field
            component={ Select }
            name="mode"
            label={ I18n.t('pro.info.adjust.modeLabel') }
            options={ modeOptions }
            fieldClasses="proLicenseFormField"
          />

          {
            mode !== 'set' && (
              <React.Fragment>
                <Field
                  component={ CustomInput }
                  name="amount"
                  label={ I18n.t('pro.info.adjust.amount') }
                  placeholder="0"
                  fieldClasses="proLicenseFormField"
                  restrictions={ [{ numeric: true }] }
                />
                <Field
                  component={ Select }
                  name="unit"
                  label={ I18n.t('pro.info.adjust.unitLabel') }
                  options={ unitOptions }
                  fieldClasses="proLicenseFormField"
                />
              </React.Fragment>
            )
          }

          {
            mode === 'set' && (
              <Field
                component={ CustomDatePicker }
                name="endDate"
                label={ I18n.t('pro.info.adjust.endDate') }
                formatDate="DD/MM/YYYY"
                fieldClasses="proLicenseFormField"
              />
            )
          }

        </div>

        <div className='proLicenseFormFields'>

          <Field
            component={ Select }
            name="planId"
            label={ I18n.t('pro.info.plan') }
            options={ this.state.planOptions }
            clearable={ true }
            onChange={ this.handlePlanChange }
            fieldClasses="proLicenseFormField"
          />

          <Field
            component={ CustomInput }
            name="maxStudents"
            label={ I18n.t('pro.info.maxStudents') }
            fieldClasses="proLicenseFormField"
            restrictions={ [{ numeric: true }] }
          />

          <Field
            component={ CustomInput }
            name="note"
            label={ I18n.t('pro.info.adjust.note') }
            placeholder={ I18n.t('pro.info.adjust.notePlaceholder') }
            fieldClasses="proLicenseFormField proLicenseFormNote"
          />

        </div>

        {
          preview && (
            <Message info size="tiny" className="proLicensePreview">
              <Icon name='calendar check outline' />
              { currentEnd
                ? I18n.t('pro.info.adjust.preview', { from: moment(currentEnd).format('DD/MM/YYYY'), to: preview.format('DD/MM/YYYY') })
                : I18n.t('pro.info.adjust.previewNew', { to: preview.format('DD/MM/YYYY') }) }
            </Message>
          )
        }

        <Form.Field>
          <Button primary floated="left" disabled={ invalid } content={ I18n.t('pro.info.adjust.apply') } icon='check' labelPosition='right' size="mini" />
          <div style={{ clear: "both" }}></div>
        </Form.Field>

      </Form>
    )
  }
}

/**
 * Validación en el cliente. Repite las reglas del backend para que el admin no
 * descubra el error después de enviar; el backend las vuelve a comprobar, porque es
 * el único sitio donde no se pueden saltar.
 */
const validate = values => {

  const errors = {};

  if (!_.includes(MODES, values.mode)) {

    errors.mode = I18n.t('validations.required');

    return errors;

  }

  if (values.mode === 'set') {

    if (!values.endDate) {

      errors.endDate = I18n.t('validations.required');

    } else if (moment(values.endDate).isBefore(moment(), 'day')) {

      errors.endDate = I18n.t('pro.info.adjust.errors.pastDate');

    } else if (moment(values.endDate).isAfter(moment().add(MAX_YEARS, 'years'))) {

      errors.endDate = I18n.t('pro.info.adjust.errors.tooFar', { years: MAX_YEARS });

    }

  } else {

    const amount = parseInt(values.amount, 10);

    if (values.amount === undefined || values.amount === '') {

      errors.amount = I18n.t('validations.required');

    } else if (!_.isFinite(amount) || amount <= 0) {

      errors.amount = I18n.t('pro.info.adjust.errors.positive');

    } else if (amount > (values.unit === 'months' ? MAX_YEARS * 12 : MAX_YEARS * 365)) {

      errors.amount = I18n.t('pro.info.adjust.errors.tooFar', { years: MAX_YEARS });

    }

  }

  if (values.maxStudents !== undefined && values.maxStudents !== '') {

    const maxStudents = parseInt(values.maxStudents, 10);

    if (!_.isFinite(maxStudents) || maxStudents < 0) {

      errors.maxStudents = I18n.t('pro.info.adjust.errors.positive');

    }

  }

  return errors;

}

const form = reduxForm({
  form: 'proLicenseForm',
  touchOnBlur: true,
  touchOnChange: true,
  validate,
  initialValues: {
    mode: 'extend',
    unit: 'days',
    amount: '',
    endDate: '',
    planId: "",
    maxStudents: "",
    note: ""
  }
})(ExtendProLicenses);

const selector = formValueSelector('proLicenseForm');

export default connect(
  state => ({
    mode: selector(state, 'mode'),
    amount: selector(state, 'amount'),
    unit: selector(state, 'unit'),
    endDate: selector(state, 'endDate')
  }),
  { openLoader, closeLoader, closeModal, openModal }
)(form);
